/**
 * Friends Actions Helpers
 * Pure functions for friends action logic
 */

import type { FriendMatchSummary, BetSelection } from '../types/friends.types';

/**
 * Check if friend is a pending invitation
 */
export const isPendingInvitation = (
  friendId: string,
  friend?: { friendshipStatus?: string; invitationId?: string }
): boolean => {
  return (
    friendId.startsWith('pending_') ||
    friend?.friendshipStatus === 'pending' ||
    friend?.invitationId !== undefined
  );
};

/**
 * Get confirmation message for friend removal
 */
export const getRemovalConfirmationMessage = (
  isPending: boolean
): string => {
  return isPending
    ? 'Are you sure you want to cancel this invitation?'
    : 'Are you sure you want to remove this friend?';
};

/**
 * Get removal alert title
 */
export const getRemovalAlertTitle = (isPending: boolean): string => {
  return isPending ? 'Cancel Invitation' : 'Remove Friend';
};

/**
 * Get removal button text
 */
export const getRemovalButtonText = (isPending: boolean): string => {
  return isPending ? 'Cancel Invitation' : 'Remove';
};

/**
 * Get success message for friend removal
 */
export const getRemovalSuccessMessage = (isPending: boolean): string => {
  return isPending
    ? 'Invitation cancelled successfully'
    : 'Friend removed successfully';
};

/**
 * Find matches with changes between current and original selections
 */
export const findChangedMatches = (
  friendMatches: FriendMatchSummary[],
  userSelections: Record<string, BetSelection>,
  originalSelections: Record<string, BetSelection>
): FriendMatchSummary[] => {
  return friendMatches.filter((match) => {
    const current = userSelections[match.matchId] ?? null;
    const original = originalSelections[match.matchId] ?? null;
    return current !== original;
  });
};

/**
 * Build predictions update payload from changed matches
 */
export const buildPredictionsUpdatePayload = (
  changedMatches: FriendMatchSummary[],
  userSelections: Record<string, BetSelection>,
  currentUserId: string
): Array<{
  challengeId: string;
  matchId: string;
  userId: string;
  prediction: Exclude<BetSelection, null>;
  outcome: Exclude<BetSelection, null>;
  homeScore: number;
  awayScore: number;
}> => {
  return changedMatches
    .map((match) => {
      const selection = userSelections[match.matchId];
      if (!selection) return null;
      return {
        challengeId: match.challengeId,
        matchId: match.matchId,
        userId: currentUserId,
        prediction: selection,
        outcome: selection,
        homeScore: selection === 'home' ? 1 : 0,
        awayScore: selection === 'away' ? 1 : 0,
      };
    })
    .filter(
      (
        item
      ): item is {
        challengeId: string;
        matchId: string;
        userId: string;
        prediction: Exclude<BetSelection, null>;
        outcome: Exclude<BetSelection, null>;
        homeScore: number;
        awayScore: number;
      } => item !== null
    );
};

/**
 * Update original selections with current selections for changed matches
 */
export const updateOriginalSelections = (
  originalSelections: Record<string, BetSelection>,
  changedMatches: FriendMatchSummary[],
  userSelections: Record<string, BetSelection>
): Record<string, BetSelection> => {
  const next = { ...originalSelections };
  changedMatches.forEach((match) => {
    next[match.matchId] = userSelections[match.matchId] ?? null;
  });
  return next;
};

